import React, { useEffect, useState } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  alpha,
  Box,
  useTheme,
  FormHelperText
} from '@mui/material';
import { BidFormErrors, EmailNotification } from './index';
import { httpRequest } from 'src/services/httpRequest';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
interface EmailNotificationFormProps {
  emailData: EmailNotification;
  setEmailData: React.Dispatch<React.SetStateAction<EmailNotification>>;
  goLiveDate: Date | null;
  errors: BidFormErrors;
  setErrors: React.Dispatch<React.SetStateAction<BidFormErrors>>;
  status: string;
}

const EmailNotificationForm: React.FC<EmailNotificationFormProps> = ({
  emailData,
  setEmailData,
  goLiveDate,
  errors,
  setErrors,
  status
}) => {
  const isGoLiveDateFilled = goLiveDate !== null && goLiveDate !== undefined;
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const hasContentError = !!errors?.content;

  // Fetch email template data when component mounts or status changes
  useEffect(() => {
    const fetchEmailTemplate = async () => {
      setLoading(true);
      const event = status === 'Awarded' ? 'Award' : 'Update';

      try {
        const response = await httpRequest(
          'GET',
          `/bid_portal/template/email?Form=UpdateBid&Event=${event}`,
          {},
          {}
        );

        if (response.status === 'success') {
          // Extract email data from the response - access the nested data array
          const templates = response.data?.data?.data || response.data?.data;

          if (templates && Array.isArray(templates) && templates.length > 0) {
            const templateData = templates[0]; // Get the first template

            // Set the email data with template values using correct field names
            setEmailData({
              subject: templateData.EmailSubject || '',
              content: templateData.EmailBody || '',
              saveType: emailData.saveType || '' // Keep the current saveType selection
            });

            // Convert the fetched HTML content to Draft.js editor state
            const contentBlock = htmlToDraft(templateData.EmailBody || '');
            const contentState = ContentState.createFromBlockArray(
              contentBlock.contentBlocks
            );
            setEditorState(EditorState.createWithContent(contentState));
          } else {
            console.warn('No template data found in response');
            // Keep existing emailData as fallback
          }
        } else {
          console.error('Failed to fetch email template:', response);
        }
      } catch (fetchError: any) {
        console.error('Error fetching email template:', fetchError);
      } finally {
        setLoading(false);
      }
    };

    fetchEmailTemplate();
  }, [status]); // Empty dependency array - only run once when component mounts

  // Convert editor state to HTML and update emailData
  const handleEditorChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    const htmlContent = draftToHtml(
      convertToRaw(newEditorState.getCurrentContent())
    );
    setEmailData((prev) => ({
      ...prev,
      content: htmlContent
    }));
  };

  // Loading state while data is being fetched
  if (loading) {
    return (
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px'
          }}
        >
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Email Subject*"
          value={emailData.subject}
          error={!!errors.subject}
          helperText={errors.subject}
          onChange={(e) => {
            setEmailData({ ...emailData, subject: e.target.value });
            if (errors.subject && e.target.value.trim() !== '') {
              setErrors((prev) => ({ ...prev, subject: '' }));
            }
          }}
        />
      </Grid>

      {/* Replace TextField for email content with Draft.js Editor */}
      <Grid item xs={12}>
        <FormControl
          fullWidth
          error={hasContentError}
          component="fieldset"
          variant="standard"
        >
          <Box
            sx={{
              border: `1px solid ${
                hasContentError
                  ? theme.palette.error.main
                  : alpha(theme.palette.primary.main, 0.23)
              }`,
              borderRadius: 1,
              '& .rdw-editor-wrapper': {
                backgroundColor: 'transparent'
              },
              '& .rdw-editor-toolbar': {
                borderBottom: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.23
                )}`,
                borderRadius: '4px 4px 0 0',
                padding: '6px',
                backgroundColor: alpha(theme.palette.background.default, 0.5)
              },
              '& .rdw-editor-main': {
                padding: '12px',
                minHeight: '250px',
                fontSize: '14px',
                lineHeight: '1.5'
              },
              '& .rdw-option-wrapper': {
                border: 'none',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1)
                }
              },
              '& .rdw-option-active': {
                backgroundColor: alpha(theme.palette.primary.main, 0.2)
              }
            }}
          >
            <Editor
              editorState={editorState}
              onEditorStateChange={handleEditorChange}
              placeholder="Enter email content..."
              toolbar={{
                options: ['inline', 'list', 'textAlign', 'history'],
                inline: { options: ['bold', 'italic', 'underline'] },
                list: { options: ['unordered', 'ordered'] },
                textAlign: { options: ['left', 'center', 'right'] }
              }}
            />
          </Box>
          {errors?.content && <FormHelperText>{errors.content}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl component="fieldset" fullWidth error={!!errors.saveType}>
          <InputLabel>Notification Options*</InputLabel>
          <Select
            value={emailData.saveType}
            onChange={(e) => {
              setEmailData({
                ...emailData,
                saveType: e.target.value as EmailNotification['saveType']
              });
              if (errors.saveType && e.target.value.trim() !== '') {
                setErrors((prev) => ({ ...prev, saveType: '' }));
              }
            }}
            label="Notification Options*"
          >
            <MenuItem value="Update and Notify" disabled={isGoLiveDateFilled}>
              Update and Notify
            </MenuItem>
            <MenuItem
              value="Update and Notify Later"
              disabled={!isGoLiveDateFilled}
            >
              Update and Notify Later
            </MenuItem>
            <MenuItem value="Update">Update</MenuItem>
          </Select>
          {errors.saveType && (
            <FormHelperText>{errors.saveType}</FormHelperText>
          )}
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default EmailNotificationForm;
